import type { Polygon as GeoJsonPolygon } from 'geojson';
import { type ComponentProps, useMemo } from 'react';

import { polygonToSVGPoints } from '~utils';
import { VISUALIZATION_CONFIG } from '../config';

import { useSvgContext } from '.';

type PolygonProps = ComponentProps<'polygon'> & {
  /** GeoJSON Polygon geometry to render */
  geometry: GeoJsonPolygon;
  /** Title for accessibility/tooltip */
  title?: string;
};

/**
 * Renders a GeoJSON polygon geometry as an SVG polygon.
 * Handles coordinate transformation and styling.
 *
 * @example
 * ```tsx
 * <Polygon
 *   geometry={geojson}
 *   fill="var(--color-primary)"
 *   className="my-polygon"
 * />
 * ```
 */
const Polygon = ({ geometry, title, ref, ...rest }: PolygonProps) => {
  const { transformer } = useSvgContext();

  const svgPoints = useMemo(() => {
    if (!geometry || !transformer) return null;
    return polygonToSVGPoints(geometry, transformer);
  }, [geometry, transformer]);

  if (!transformer || !svgPoints) return null;

  return (
    <polygon
      ref={ref}
      points={svgPoints}
      fill={rest.fill ?? 'var(--color-destructive-container)'}
      fillOpacity={rest.fillOpacity ?? VISUALIZATION_CONFIG.POLYGON.FILL_OPACITY}
      stroke={rest.stroke ?? 'var(--color-veil)'}
      strokeWidth={rest.strokeWidth ?? 2}
      vectorEffect="non-scaling-stroke"
      {...rest}
    >
      {title && <title>{title}</title>}
    </polygon>
  );
};
Polygon.displayName = 'Polygon';

export { Polygon, type PolygonProps };
